/**
 * Dialog Component (shadcn/ui compatible)
 * Modal dialog for displaying content
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}

const Dialog = ({ open, onOpenChange, children }: DialogProps) =>
{
  React.useEffect(() =>
  {
    if (open)
    {
      document.body.style.overflow = 'hidden';
    }
    else
    {
      document.body.style.overflow = 'unset';
    }
    return () =>
    {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  React.useEffect(() =>
  {
    const handleEscape = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape' && open)
      {
        onOpenChange?.(false);
      }
    };

    if (open)
    {
      document.addEventListener('keydown', handleEscape);
    }

    return () =>
    {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0" onClick={() => onOpenChange?.(false)} />
      {/* Content */}
      {children}
    </div>
  );
};

const DialogContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'relative z-50 bg-white shadow-xl border border-gray-300',
        'w-full max-w-[563px] p-6',
        'dark:bg-gray-900',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);
DialogContent.displayName = 'DialogContent';

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn('text-lg font-semibold leading-none tracking-tight text-[#4887be]', className)}
      {...props}
    />
  ),
);
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-gray-500 dark:text-gray-400', className)}
    style={{ fontFamily: "'Roboto', Arial, sans-serif", color: '#414042', fontSize: '14px' }}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

// ConfirmDialog component
interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'default' | 'destructive';
}

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant: _confirmVariant = 'default',
}: ConfirmDialogProps) =>
{
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <div className="flex items-start justify-between mb-2">
          <DialogTitle>{title}</DialogTitle>
          <button
            onClick={onClose}
            style={{
              fontSize: '20px',
              lineHeight: 1,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              color: '#aaa',
              fontWeight: 400,
            }}
            onMouseEnter={e =>
            {
              e.currentTarget.style.color = '#777';
            }}
            onMouseLeave={e =>
            {
              e.currentTarget.style.color = '#aaa';
            }}
          >
            &times;
          </button>
        </div>
        <DialogDescription>{description}</DialogDescription>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="dialog-btn-cancel"
            style={{
              background: '#929498',
              border: '1px solid #929498',
              borderRadius: 0,
              color: '#fff',
              fontSize: '12px',
              lineHeight: 1.5,
              margin: 0,
              outline: 'none',
              padding: '4px 12px',
            }}
            onMouseEnter={e =>
            {
              const el = e.currentTarget;
              el.style.background = '#77797c';
              el.style.borderColor = '#77797c';
            }}
            onMouseLeave={e =>
            {
              const el = e.currentTarget;
              el.style.background = '#929498';
              el.style.borderColor = '#929498';
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: '#62accb',
              border: '1px solid #62accb',
              borderRadius: 0,
              color: '#fff',
              fontSize: '12px',
              lineHeight: 1.5,
              margin: 0,
              outline: 'none',
              padding: '4px 12px',
            }}
            onMouseEnter={e =>
            {
              const el = e.currentTarget;
              el.style.background = '#508ca6';
              el.style.borderColor = '#508ca6';
            }}
            onMouseLeave={e =>
            {
              const el = e.currentTarget;
              el.style.background = '#62accb';
              el.style.borderColor = '#62accb';
            }}
          >
            {confirmText}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
ConfirmDialog.displayName = 'ConfirmDialog';

export { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, ConfirmDialog };
